import type { SvgIconComponent } from '@mui/icons-material';
import {
  Category as CategoryIcon,
  EnergySavingsLeafOutlined as LeafIcon,
  Inventory2Outlined as BoxIcon,
  RecyclingOutlined as RecyclingIcon,
  HardwareOutlined as MetalIcon,
  ForestOutlined as WoodIcon,
  WaterDropOutlined as SludgeIcon,
  LayersOutlined as MixedIcon,
  ScienceOutlined as ChemicalIcon,
  DevicesOutlined as EwasteIcon,
  OilBarrelOutlined as OilIcon,
  WarningAmberOutlined as HazardIcon,
  DonutLargeOutlined as RubberIcon,
  CheckroomOutlined as TextileIcon,
  LocalBarOutlined as GlassIcon,
  MoreHorizOutlined as OtherIcon,
  HelpOutlineOutlined as UncategorizedIcon,
} from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material';
import { MaterialsMainCategoriesTypes } from '../types/recycling.type';

export interface MaterialCategoryTheme {
  color: string;
  Icon: SvgIconComponent;
}

const CATEGORY_THEME_MAP: Record<string, MaterialCategoryTheme> = {
  [MaterialsMainCategoriesTypes.ORGANIC]: { color: '#15803d', Icon: LeafIcon },
  [MaterialsMainCategoriesTypes.CARDBOARD]: { color: '#9A3412', Icon: BoxIcon },
  [MaterialsMainCategoriesTypes.PLASTIC]: { color: '#0369A1', Icon: RecyclingIcon },
  [MaterialsMainCategoriesTypes.METAL]: { color: '#475569', Icon: MetalIcon },
  [MaterialsMainCategoriesTypes.WOOD]: { color: '#92400E', Icon: WoodIcon },
  [MaterialsMainCategoriesTypes.SLUDGE]: { color: '#6B7280', Icon: SludgeIcon },
  [MaterialsMainCategoriesTypes.MIXED]: { color: '#7C3AED', Icon: MixedIcon },
  [MaterialsMainCategoriesTypes.CHEMICAL]: { color: '#BE185D', Icon: ChemicalIcon },
  [MaterialsMainCategoriesTypes.EWASTE]: { color: '#0F766E', Icon: EwasteIcon },
  [MaterialsMainCategoriesTypes.OIL]: { color: '#A16207', Icon: OilIcon },
  [MaterialsMainCategoriesTypes.HAZARDOUS_WASTE]: { color: '#DC2626', Icon: HazardIcon },
  [MaterialsMainCategoriesTypes.RUBBER]: { color: '#374151', Icon: RubberIcon },
  [MaterialsMainCategoriesTypes.TEXTILES]: { color: '#DB2777', Icon: TextileIcon },
  [MaterialsMainCategoriesTypes.GLASS]: { color: '#0891B2', Icon: GlassIcon },
  [MaterialsMainCategoriesTypes.OTHER]: { color: '#64748B', Icon: OtherIcon },
};

const UNCATEGORIZED_KEY = '__uncategorized__';

export function getMaterialCategoryTheme(
  categoryKey: string,
  theme: Theme
): MaterialCategoryTheme {
  const normalized = categoryKey.toLowerCase();
  if (normalized === UNCATEGORIZED_KEY) {
    return { color: theme.palette.text.secondary, Icon: UncategorizedIcon };
  }
  return (
    CATEGORY_THEME_MAP[normalized] ?? {
      color: theme.palette.primary.main,
      Icon: CategoryIcon,
    }
  );
}

export interface MaterialCategorySurface {
  color: string;
  Icon: SvgIconComponent;
  headerBg: string;
  headerBorder: string;
  iconBg: string;
  iconBorder: string;
  tableHeadBg: string;
  rowHoverBg: string;
  accentBorder: string;
}

export function getMaterialCategorySurface(
  categoryKey: string,
  theme: Theme
): MaterialCategorySurface {
  const { color, Icon } = getMaterialCategoryTheme(categoryKey, theme);
  return {
    color,
    Icon,
    headerBg: alpha(color, 0.06),
    headerBorder: alpha(color, 0.22),
    iconBg: alpha(color, 0.14),
    iconBorder: alpha(color, 0.28),
    tableHeadBg: alpha(color, 0.07),
    rowHoverBg: alpha(color, 0.06),
    accentBorder: color,
  };
}
